import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'
import sortBy from 'lodash/sortBy.js'

import {setForm, updateForm, formsSelector, getState} from '../store.js'
import {fetchAllApiverson} from '../common/fetchAll.js'
import {getNounsFromValue, tokenizeOptions} from '../common/tokenizeOptions.js'
import apiverson from '../common/apiverson.js'
import {buildPath} from '../common/querystring.js'
import {showGlobalError} from '../ordoro/GlobalErrorMessage.js'
import {showMessageToast} from '../ordoro/Header/Toast/index.js'
import {showConfirmMessageModal} from '../common/components/Modal/ConfirmMessageModal.js'
import {getUnique} from '../common/getUnique.js'

export const FILTER_CONFIGS = 'filter_configs'
export const NEW_FILTER_CONFIG_ID = 'NEW_FILTER_CONFIG_ID'

export function filterConfigsSelector(state) {
  return formsSelector(state)[FILTER_CONFIGS] || filterConfigsSelector.default
}
filterConfigsSelector.default = {}

export function filterConfigSelector(state, {filterConfigID}) {
  return filterConfigsSelector(state)[filterConfigID]
}

export const filterConfigsSortedByIDSelector = createSelector(
  filterConfigsSelector,
  (filterConfigs) => sortBy(filterConfigs, 'id'),
)

export const filterConfigOptionsSelector = createSelector(
  filterConfigsSortedByIDSelector,
  (filterConfigs) =>
    filterConfigs.map((filterConfig) => ({
      value: filterConfig.id,
      display: filterConfig.name,
      entity: filterConfig,
      type: FILTER_CONFIGS,
      nouns: getNounsFromValue(`${filterConfig.name}`),
    })),
)

export const filterConfigOptionTokensSelector = createSelector(
  filterConfigOptionsSelector,
  (filterConfigOptions) => tokenizeOptions(filterConfigOptions),
)

export const filterConfigOptionsByTypeSelector = createSelector(
  filterConfigOptionsSelector,
  (options) =>
    options.reduce((prev, option) => {
      const type = option.entity.type

      if (!type) {
        return prev
      }

      if (prev[type]) {
        prev[type].push(option)
      } else {
        prev[type] = [option]
      }

      return prev
    }, {}),
)

export function filterConfigOptionsForTypeSelector(state, {type}) {
  return (
    filterConfigOptionsByTypeSelector(state)[type] ||
    filterConfigOptionsForTypeSelector.default
  )
}
filterConfigOptionsForTypeSelector.default = []

const defaultNameRegEx = /^Filter (\d+)$/
function getUniqueFilterConfigName(filterConfigs) {
  return getUnique(
    'Filter ',
    defaultNameRegEx,
    Object.values(filterConfigs).map(({name}) => name),
  )
}

export function filterConfigNameSelector(state, {filterConfigID}) {
  const filterConfigs = filterConfigsSelector(state)

  if (filterConfigID === NEW_FILTER_CONFIG_ID) {
    return getUniqueFilterConfigName(filterConfigs)
  }

  return filterConfigs[filterConfigID]?.name || ''
}

export function setFilterConfigs(filterConfigs) {
  setForm(FILTER_CONFIGS, keyBy(filterConfigs, 'id'))
}

export function setFilterConfig(filterConfig) {
  updateForm(FILTER_CONFIGS, {[filterConfig.id]: filterConfig})
}

export async function getFilterConfigs() {
  const filterConfigs = await fetchAllApiverson(
    '/filter_config/',
    'filter_config',
  )

  setFilterConfigs(filterConfigs)
}

export async function saveFilterConfig(filterConfigID, params) {
  try {
    const isNew = filterConfigID === NEW_FILTER_CONFIG_ID

    if (!isNew) {
      delete params.type
    }

    const {json} = isNew
      ? await apiverson.post('/filter_config', params)
      : await apiverson.put(
          buildPath(['filter_config', filterConfigID]),
          params,
        )

    setFilterConfig(json)

    showMessageToast(
      isNew ? 'Saved filter was created' : 'Saved filter was updated',
    )
  } catch (err) {
    showGlobalError(
      {
        summary: 'Something went wrong while creating or updating a filter.',
        details: err.message,
      },
      err,
    )
  }
}

export async function deleteFilterConfig(filterConfigID) {
  await showConfirmMessageModal({
    title: 'Delete Filter',
    Message: () => (
      <div>Are you sure you want to delete this saved filter set?</div>
    ),
    confirmText: 'Delete',
    cancelText: 'Cancel',
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      await apiverson.delete(buildPath(['filter_config', filterConfigID]))

      const filterConfigs = filterConfigsSortedByIDSelector(getState())

      setFilterConfigs(filterConfigs.filter(({id}) => id !== filterConfigID))
    },
  })
}
